/* v8 Path Q — shared current-period highlight styles.
   MetricHorizonTable + FormalStatementTable both highlight the
   "current period" column in their sticky <thead>. Keeping the
   style tokens in one file keeps the visual treatment identical
   across every comparative table AND lets us tune it in one place.

   v8 Path S bugfix — semi-transparent backgrounds on sticky <th>
   let the body content bleed through when scrolled (the "● 2026-04"
   label appeared overlapping the first data row). The header
   classes now use a precomputed OPAQUE colour (rgba(240,180,41,0.18)
   composited on #1c1c30 = approximately #3d3420) so scroll content
   stays fully hidden under the sticky header. The body cells
   keep the lighter tint so the column highlight still feels
   subtle. */
export const CURRENT_PERIOD_HEADER_CLS =
  'bg-[#3d3420] text-accent-gold';

export const DEFAULT_PERIOD_HEADER_CLS =
  'bg-[#1c1c30] text-text-dim';

/* Body-row current-period tint — kept semi-transparent; it layers on top
   of the solid row bg. Exported so MetricHorizonTable + FormalStatementTable
   can use the same tint. */
export const CURRENT_PERIOD_CELL_TINT =
  'bg-[rgba(240,180,41,0.08)]';

/* v8 Path X — independent gridline right under the sticky thead.
   Painted via INSET box-shadow inside each <th>, not via border-bottom.
   Why inset: with `border-collapse: collapse`, a real `border-bottom`
   collapses with the first tbody row's `border-top` and browsers paint
   the shared border as part of the tbody cell — meaning it scrolls with
   the body instead of pinning to the sticky thead. An inset shadow is
   drawn INSIDE the thead cell's own box, always attached to the thead,
   never participating in collapse.

   v8 Path Z — dropped the outer drop-shadow below the thead. With the
   sticky H1/H2 rows now pinning flush against the thead, a drop-shadow
   would create a visible gap between the column-header line and the
   section heading. The inset 1px line alone is crisp enough. */
export const THEAD_BOTTOM_LINE =
  'shadow-[inset_0_-1px_0_rgb(58,64,80)]';

/* Sticky frozen cells own their boxed gridlines. Collapsed table borders can
   be painted by horizontally scrolling neighbours, so the frozen cell's text
   stays pinned while visible border lines drift. This inset shadow paints all
   four gridlines inside the sticky cell itself, plus the right-edge scroll
   shadow, so the whole boxed column freezes together. */
export const STICKY_BOX_GRID =
  '!border-0 shadow-[inset_1px_0_0_rgba(255,255,255,0.06),inset_-1px_0_0_rgba(255,255,255,0.06),inset_0_1px_0_rgba(255,255,255,0.06),inset_0_-1px_0_rgba(255,255,255,0.06),6px_0_8px_-3px_rgba(0,0,0,0.45)]';

/* The top-left "corner" cell uses the same sticky boxed grid contract.
   Exported so both tables apply an identical treatment to the corner. */
export const THEAD_CORNER_SHADOW = STICKY_BOX_GRID;

/* v8 Path AA.1 — unified thead vertical padding. FormalStatementTable
   used `py-[0.3rem]` (→ 24px thead) while MetricHorizonTable used
   `py-[0.35rem]` (→ 25px thead). The 1px mismatch produced a 1px gap
   under the Formal thead because the sticky H1/H2 `top-[25px]` overshoots
   Formal's thead bottom by 1px. Exporting the single source of truth
   here; both tables consume `THEAD_VERT_PAD`. */
export const THEAD_VERT_PAD = 'py-[0.35rem]';

/* Shared cell-border token. FormalStatementTable uses `border-white/[0.06]`;
   MetricHorizonTable previously bumped to `0.12` for visibility, but that
   produced heavier-looking vertical rules than the 3-statement tables. The
   two now agree on 0.06 — the thead's inset-shadow line is the visible
   divider, the in-cell borders are a subtle grid. */
export const CELL_BORDER = 'border-white/[0.06]';

/* v8 Path Y — cascading sticky section headers.
   H1 = section heading ("REVENUE", "OPERATING EXPENSES", "ASSETS", …)
   H2 = section total    ("Total revenue", "Total operating expenses", …)
   Both stick just below the thead. When the user scrolls past a section,
   its H1 pins; the section's H2 total arriving next pushes H1 up (they
   share the same sticky `top`, so CSS natively hands the slot to the
   newer one); the next section's H1 then pushes H2 up. Data rows (H3)
   never stick.

   Why `top: 32px`: thead row is ~30px tall (py-[0.35rem] + 0.72rem text).
   32px leaves a 1-2px buffer so the sticky H never visually overlaps
   the thead's inset bottom line.

   z-indices: thead sits above at z-[20] / z-[30] (corner). Sticky H rows
   sit at z-[18] (regular cells) / z-[19] (sticky-left label + Trend cells) —
   below the thead, above the scrolling body. */
/* v8 Path AB — CSS custom property for per-row dynamic sticky top.
   `useStickyCascade` writes `--sticky-h-top` onto each `<tr data-sticky-row>`
   during scroll. Every sticky `<td>` in that row reads the inherited
   value via `top-[var(--sticky-h-top,…)]`. When the next section's
   sticky row collides with the current one, the hook sets a NEGATIVE
   top on the current row to push it up out of view — so sections
   supersede each other (true push-upward cascade), not overlay.

   v8 Path AD — fallback = 24px (1px LESS than thead's ~25px height).
   This causes the H1/H2 row to OVERLAP the thead by 1px so there's
   no possible sub-pixel rendering seam between them. Classic CSS fix
   for "bleeding gap" between two stacked sticky layers. */
export const STICKY_H_CELL      = 'sticky top-[var(--sticky-h-top,24px)] z-[18]';
export const STICKY_H_CELL_LEFT = 'sticky left-0 top-[var(--sticky-h-top,24px)] z-[19]';

/* Shared sticky Trend column cells. Each table provides
   `--lens-label-col-width`; these tokens freeze Trend immediately after
   that label column. Keeping the left offset here prevents a source drift
   where a Trend cell renders sticky but still scrolls horizontally.

   v8 Path AL — Trend owns its boxed gridlines. The shared sticky box token is
   aliased here so existing Trend contracts keep their source-level name. */
export const STICKY_TREND_HEADER_CELL =
  'sticky left-[var(--lens-label-col-width)] top-0 z-[25] bg-[#1c1c30]';

export const STICKY_TREND_BODY_CELL =
  'sticky left-[var(--lens-label-col-width)] z-[9] !bg-[#1c1c30]';

export const STICKY_TREND_SECTION_CELL =
  'sticky left-[var(--lens-label-col-width)] top-[var(--sticky-h-top,24px)] z-[19] !bg-[#1c1c30]';

export const STICKY_TREND_BOX_GRID = STICKY_BOX_GRID;

/* Solid pre-composited colour for the sticky H current-period cell.
   Scrolling content must not bleed through a sticky row, so we cannot
   use the semi-transparent gold tint in CURRENT_PERIOD_CELL_TINT. This
   is the same opaque value CURRENT_PERIOD_HEADER_CLS uses. */
export const CURRENT_PERIOD_STICKY_BG = 'bg-[#3d3420]';
